import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import Select from "react-select"
import { useField } from "react-form"
import * as ReactIcon from "react-icons/md"
import _ from "lodash"
import Label from "../../../common/form/label/label"
import { LocationContext } from "../../../common/location-provider/location-provider"
import asSearchFilter from "../as-search-filter/as-search-filter"
import { getOrganizations } from "../../../../services/organizations"
import "./search-organization.scss"

const SearchOrganization = ({
  setValue,
  value,
  fieldName,
  fieldOptions,
  label,
  placeholder,
  icon,
  isClearable,
  onChange,
  componentid,
}) => {
  const state = useContext(LocationContext)
  const fieldInstance = useField(fieldName, fieldOptions)
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  // GET /organizations answers with a { data, meta } envelope, but some
  // callers already hand back a bare array, so accept either
  const unwrap = response =>
    _.isArray(response) ? response : _.get(response, "data", [])

  const toOptions = response =>
    _.sortBy(
      _.map(
        _.filter(unwrap(response), org => org && org.name),
        org => ({ value: org.id || org._id, label: org.name })
      ),
      opt => _.toLower(opt.label)
    )

  useEffect(() => {
    let isCurrent = true
    getOrganizations()
      .then(organizations => {
        console.log("ORGANZIATIONS", organizations )
        if (!isCurrent) return
        setOptions(toOptions(organizations))
        setIsLoading(false)
      })
      .catch(error => {
        console.log(
          "SearchOrganization ERROR: organizations not loaded",
          error
        )
        if (!isCurrent) return
        setOptions([])
        setIsLoading(false)
      })
    return () => {
      isCurrent = false
    }
  }, [])

  // asSearchFilter holds the query value (the organization id); the form field
  // holds the selected option so Clear and re-mounts keep the label showing
  const getSelectedId = () => {
    const current = !_.isNil(value) && value !== "" ? value : fieldInstance.value
    if (_.isNil(current) || current === "") return null
    return _.isObject(current) ? current.value : current
  }

  const getSelected = () => {
    const id = getSelectedId()
    if (_.isNil(id) || id === "") return null
    return options.find(opt => String(opt.value) === String(id)) || null
  }

  const handleChange = selected => {
    fieldInstance.setValue(selected || { value: "" })
    // an empty string makes asSearchFilter fall back to match_all
    setValue(selected ? selected.value : "")
    if (onChange) onChange(selected)
  }

  const toId = v => {
    const id = _.isObject(v) ? v.value : v
    return _.isNil(id) ? "" : id
  }

  // the Clear button resets the form values directly rather than going through
  // handleChange, so mirror the form field into the query when the two diverge
  const fieldValue = fieldInstance.value
  useEffect(() => {
    if (String(toId(fieldValue)) !== String(toId(value))) {
      setValue(toId(fieldValue))
    }
  }, [fieldValue])

  // restores the filter when the search page is re-entered with saved state
  useEffect(() => {
    if (!state) return
    const restored = state[state.useComponentId ? componentid : fieldName]
    const id = toId(restored)
    if (id !== "" && String(id) !== String(toId(value))) {
      fieldInstance.setValue({ value: id })
      setValue(id)
    }
  }, [state])

  return (
    <div className="search-organization">
      <Label label={label} />
      <div className="select-group">
        <div className="icon prepend-icon">
          {React.createElement(ReactIcon[icon || "MdSearch"])}
        </div>
        <Select
          className="organization-select"
          classNamePrefix="organization-select"
          name={fieldName}
          value={getSelected()}
          options={options}
          onChange={handleChange}
          isClearable={isClearable}
          isLoading={isLoading}
          placeholder={placeholder}
          noOptionsMessage={() => "No organizations found"}
        />
      </div>
    </div>
  )
}

SearchOrganization.defaultProps = {
  isClearable: true,
}

SearchOrganization.propTypes = {
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  isClearable: PropTypes.bool,
  onChange: PropTypes.func,
}

export default asSearchFilter(SearchOrganization)
